import React, { useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useIntl } from '@utils/localize';
import get from 'lodash/get';

import getLocalizedData from '@utils/localize';
import useSearchSortFilter from '@utils/searchSortFilter';
import { TranslationContext } from '@utils/useTranslations';
import Container from '@components/Container';
import EmptyState from '@components/EmptyState';
import Footer from '@components/Footer';
import Navigation, { NavSpacer } from '@components/Navigation';
import SearchSortFilter from '@components/SearchSortFilter';
import SEO from '@components/SEO';
import PersonBubbleBlock from '@components/block/PersonBubbleBlock';

function StaffPage({ banner, navItems }) {
  const rawData = useStaticQuery(graphql`
    query StaffQuery {
      allSanityStaff(sort: { fields: name, order: ASC }) {
        edges {
          node {
            _id
            email
            departmentReference {
              department {
                title {
                  en
                  vi
                  zh_Hans
                }
              }
              positionTitle {
                en
                vi
                zh_Hans
              }
              rankingNumber
            }
            description {
              en
              vi
              zh_Hans
              _type
            }
            yearAppointed
            title {
              en
              vi
              zh_Hans
              _type
            }
            name
            imageExtended {
              altText {
                en
                vi
                zh_Hans
                _type
              }
              image  {
                asset {
                  fluid(
                    maxWidth: 800,
                    sizes: "(max-width: 600px) 100vw, 50vw"
                    ){
                    ...GatsbySanityImageFluid
                  }
                }
              }
            }
          }
        }
      }
      allSanityDepartment {
        nodes {
          title {
            en
          }
        }
      }
    }
  `);

  const intl = useIntl();
  const data = getLocalizedData(rawData, intl.locale);

  const departments = get(data, 'allSanityDepartment.nodes', [])
    .map((department) => department.title?.[intl.locale] || department.title?.en)
    .filter(Boolean)
    .sort();

  const processStaff = (nodes, locale) => {
    return nodes.map(({ node }) => {
      const perDepartment = node.departmentReference.map(department => ({
        departmentName: department.department?.title?.[locale],
        ranking: department.rankingNumber,
        positionTitle: department.positionTitle?.[locale] || null,
      }));

      const departmentNames = perDepartment
        .map(department => department.departmentName)
        .filter(Boolean);

      return {
        ...node,
        perDepartment,
        departmentNames,
      };
    });
  };

  const staff = useMemo(() => {
    return processStaff(get(data, 'allSanityStaff.edges', []), intl.locale);
  }, [data, intl.locale]);

  const getGroupedStaffByDepartment = (departments, items, filter) => {
    return departments
      .filter((department) => {
        return filter?.length ? filter.includes(department) : true;
      })
      .map((department) => {
        const deptItems = items
          .filter((item) =>
            item.perDepartment.some(
              (d) => d.departmentName === department,
            ),
          )
          .map((item) => {
            const match = item.perDepartment.find(
              (innerDepartment) => innerDepartment.departmentName === department,
            );
            const fallbackTitle =
              typeof item.title === 'string' ? item.title : '';
            return {
              ...item,
              title: match?.positionTitle || fallbackTitle,
              department,
              rankingInThisDepartment: match?.ranking,
            };
          })
          .sort((a, b) => a.rankingInThisDepartment - b.rankingInThisDepartment);

        return { department, items: deptItems };
      })
      .filter(({ items }) => items.length > 0);
  };

  const leadershipFirst = [
    ...departments.filter((d) => d === 'Leadership'),
    ...departments.filter((d) => d !== 'Leadership'),
  ];

  const { controller, items } = useSearchSortFilter(staff, {
    initialFilter: {
      departmentNames: [],
    },
    initialSort: { key: 'name', order: 'asc' },
    searchFields: ['departmentNames', 'name', 'title'],
  });

  const translationContext = {
    vi: true,
    zh_Hans: true,
  };

  const isSearching =
    controller.state.search.length > 0 ||
    (controller.state.filter.departmentNames?.length ?? 0) > 0;

  const groupedStaff = useMemo(() => {
    return getGroupedStaffByDepartment(
      leadershipFirst,
      items,
      controller.state.filter.departmentNames,
    );
  }, [leadershipFirst, items, controller.state.filter.departmentNames]);

  return (
    <>
      <TranslationContext.Provider value={translationContext}>
        <Navigation banner={banner} navItems={navItems} />
        <main>
          <SEO title="Faculty and Staff" />
          <NavSpacer />
          <SearchSortFilter
            controller={controller}
            filters={[
              {
                key: 'departmentNames',
                title: intl.formatMessage({ id: 'departments' }),
                items: departments.map((department) => ({
                  name: department,
                  value: department,
                })),
              },
            ]}
            search={{ title: intl.formatMessage({ id: 'facultyAndStaff.search.title' }) }}
          />
          <Container centered>
            {groupedStaff.length > 0 ? (
              groupedStaff.map(({ department, items }) => (
                <React.Fragment key={department}>
                  <PersonBubbleBlock
                    key={`${department}-${isSearching ? 'search' : 'default'}`}
                    defaultOpen={isSearching || department === 'Leadership'}
                    items={items}
                    showItemsInsideAccordion
                    bubblesPerRow={department === 'Leadership' ? 4 : 5}
                  />
                </React.Fragment>
              ))
            ) : (
              <EmptyState />
            )}
          </Container>
        </main>
        <Footer />
      </TranslationContext.Provider>
    </>
  );
}

export default StaffPage;
